import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { scheduleKeys } from "./queryKeys";
import {
  fetchAllShiftCodes,
  createShiftCode,
  updateShiftCode,
  deleteShiftCode,
  hardDeleteShiftCode,
  type ShiftCodeInput,
} from "@/services/schedule/shiftCodeService";

/**
 * Hook to fetch all shift codes for the company
 */
export const useShiftCodeList = (companyId: string | undefined) => {
  return useQuery({
    queryKey: scheduleKeys.shiftCodes(companyId || ''),
    queryFn: fetchAllShiftCodes,
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create a new shift code
 */
export const useCreateShiftCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createShiftCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      toast.success("Shift code created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create shift code");
    },
  });
};

/**
 * Hook to update an existing shift code
 */
export const useUpdateShiftCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<ShiftCodeInput> }) =>
      updateShiftCode(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      toast.success("Shift code updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update shift code");
    },
  });
};

/**
 * Hook to delete (soft delete) a shift code
 */
export const useDeleteShiftCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteShiftCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      toast.success("Shift code deactivated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to deactivate shift code");
    },
  });
};

/**
 * Hook to permanently delete a shift code
 */
export const useHardDeleteShiftCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: hardDeleteShiftCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      toast.success("Shift code permanently deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to permanently delete shift code");
    },
  });
};
